import { createContext, useCallback, useEffect, useMemo, useState } from "react";
import { en } from "./locales/en";
import { fr } from "./locales/fr";
import type { PepLocale, PepTranslateFn, PepTranslationKey } from "./types";

const STORAGE_KEY = "itaas.locale";

const dictionaries = { fr, en };

export interface PepI18nContextValue {
  locale: PepLocale;
  setLocale: (locale: PepLocale) => void;
  t: PepTranslateFn;
}

export const PepI18nContext = createContext<PepI18nContextValue | null>(null);

function readLocale(): PepLocale {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === "en" || stored === "fr") return stored;
  const browser = navigator.language.toLowerCase();
  return browser.startsWith("fr") ? "fr" : "en";
}

function interpolate(template: string, params?: Record<string, string | number>): string {
  if (!params) return template;
  return Object.entries(params).reduce(
    (text, [key, value]) => text.replace(new RegExp(`\\{${key}\\}`, "g"), String(value)),
    template,
  );
}

export function PepI18nProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = useState<PepLocale>(readLocale);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = useCallback((next: PepLocale) => {
    setLocaleState(next);
    localStorage.setItem(STORAGE_KEY, next);
  }, []);

  const t = useMemo<PepTranslateFn>(() => {
    const primary = dictionaries[locale];
    const fallback = dictionaries.en;
    return (key: PepTranslationKey, params?) => {
      const template = primary[key] ?? fallback[key] ?? key;
      return interpolate(template, params);
    };
  }, [locale]);

  const value = useMemo(() => ({ locale, setLocale, t }), [locale, setLocale, t]);

  return <PepI18nContext.Provider value={value}>{children}</PepI18nContext.Provider>;
}
